import { motion } from "framer-motion";
import {
  LayoutTemplate,
  Boxes,
  Bot,
  ShoppingBag,
  CalendarClock,
  Gauge,
  ArrowUpRight,
} from "lucide-react";

const services = [
  {
    icon: LayoutTemplate,
    title: "Marketing Websites",
    body: "High-conversion landing pages and full brand sites that ship in under a week.",
    price: "Custom Quote",
  },
  {
    icon: CalendarClock,
    title: "Booking Platforms",
    body: "Salon, clinic, gym or restaurant booking flows with an admin dashboard.",
    price: "Tailored Pricing",
  },
  {
    icon: ShoppingBag,
    title: "E-commerce",
    body: "Fast, brand-forward stores with Stripe payments, inventory and dashboards.",
    price: "Project-Based Pricing",
  },
  {
    icon: Boxes,
    title: "Web Apps & Dashboards",
    body: "Internal tools, portals and dashboards on FastAPI + MongoDB.",
    price: "Custom Solution",
  },
  {
    icon: Bot,
    title: "AI Features",
    body: "Chat assistants, generative UI, semantic search and workflows integrated deep.",
    price: "Available as an Add-on",
  },
  {
    icon: Gauge,
    title: "Speed & SEO Rescue",
    body: "Take an existing slow site and turn it into a fast, Google-friendly asset.",
    price: "Website Audit Available",
  },
];

const scrollToContact = () => {
  const el = document.getElementById("contact");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 lg:py-40 border-t border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-14 lg:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8"
          >
            <div className="font-mono-tag text-xs text-[#E2FF31] mb-6">
              // 002 — What I Build
            </div>
            <h2 className="font-display font-medium text-5xl sm:text-6xl md:text-7xl leading-[0.95]">
              Six ways to turn your idea
              <br />
              <span className="text-white/40">into a real, revenue-ready site.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-4 text-white/60 text-base leading-relaxed"
          >
            Fixed-scope engagements with clear, transparent process. Every
            build ships with premium UI, mobile-first responsive design and
            one year of free bug support.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
          {services.map((s, i) => (
            <motion.button
              key={s.title}
              type="button"
              data-testid={`service-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              data-cursor="Enquire"
              onClick={scrollToContact}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              className="group relative text-left p-7 lg:p-8 rounded-2xl border border-white/10 bg-[#0C0C0C]/60 hover:border-[#E2FF31]/50 hover:bg-[#E2FF31]/[0.03] transition-all overflow-hidden"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 rounded-xl border border-white/10 bg-black flex items-center justify-center group-hover:border-[#E2FF31]/50 group-hover:bg-[#E2FF31]/10 transition-all">
                  <s.icon
                    size={20}
                    className="text-[#E2FF31] group-hover:scale-110 transition-transform"
                  />
                </div>
                <ArrowUpRight
                  size={18}
                  className="text-white/40 group-hover:text-[#E2FF31] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                />
              </div>
              <div className="font-display text-2xl font-medium mb-3">
                {s.title}
              </div>
              <p className="text-white/60 text-sm leading-relaxed mb-6">
                {s.body}
              </p>
              <div className="pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                <span className="text-[11px] tracking-wide text-white/60 group-hover:text-[#E2FF31] transition-colors italic font-light">
                  {s.price}
                </span>
                <span className="font-mono-tag text-[10px] text-[#E2FF31] opacity-0 group-hover:opacity-100 translate-x-[-4px] group-hover:translate-x-0 transition-all whitespace-nowrap">
                  Get in touch →
                </span>
              </div>
              {/* Hover shine */}
              <span
                aria-hidden
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "radial-gradient(600px circle at 20% 0%, rgba(226,255,49,0.06), transparent 40%)",
                }}
              />
            </motion.button>
          ))}
        </div>

        {/* Premium consultation callout */}
        <motion.div
          data-testid="services-consultation-callout"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative mt-20 lg:mt-28 overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0C0C0C] via-black to-[#0C0C0C]"
        >
          {/* Ambient light */}
          <span
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(600px circle at 20% 30%, rgba(226,255,49,0.08), transparent 45%), radial-gradient(500px circle at 90% 80%, rgba(226,255,49,0.05), transparent 50%)",
            }}
          />
          {/* Faint grid */}
          <span
            aria-hidden
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, #E2FF31 1px, transparent 1px), linear-gradient(to bottom, #E2FF31 1px, transparent 1px)",
              backgroundSize: "72px 72px",
            }}
          />

          <div className="relative px-8 py-14 lg:px-16 lg:py-20 flex flex-col items-center text-center">
            <div className="font-mono-tag text-[10px] text-[#E2FF31] mb-6">
              // Bespoke engagements
            </div>
            <h3 className="font-display font-medium text-4xl sm:text-5xl lg:text-6xl leading-[0.98] max-w-3xl">
              Every project is{" "}
              <span className="italic font-light text-[#E2FF31]">unique.</span>
            </h3>
            <p className="mt-6 text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl">
              I create tailored solutions based on your business goals, required
              features and timeline. Book a free consultation to receive a
              personalized quotation.
            </p>
            <button
              data-testid="services-cta-request-quote"
              data-cursor="Free quote"
              onClick={scrollToContact}
              className="btn-primary mt-10 text-base"
            >
              Request a Free Quote
              <span className="arrow">→</span>
            </button>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono-tag text-[10px] text-white/40">
              <span className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E2FF31]" /> No
                obligation
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E2FF31]" /> Reply
                within 24h
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E2FF31]" /> Fixed
                quote before we start
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
